import React from "react";
import {
  DataGridBlock,
  DataGridColumn,
  DataGridRow as DataGridBlockRow,
  WiseOwlAction,
  WiseOwlActionType,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHead,
  DataGridHeader,
  DataGridRow,
} from "@twilio-paste/core/data-grid";
import { Button } from "@twilio-paste/core/button";
import { useWiseOwlContext } from "../../../hooks";
import { styled } from "@twilio-paste/core/styling-library";
import { Box } from "@twilio-paste/core/box";
import { InformationIcon } from "@twilio-paste/icons/esm/InformationIcon";
import {
  PopoverContainer,
  PopoverButton,
  Popover,
} from "@twilio-paste/core/popover";
import { Text } from "@twilio-paste/core/text";

interface DataGridRendererProps {
  block: DataGridBlock;
}

const StyledContainer = styled(Box)`
  [data-paste-element="DATA_GRID_CELL"]:not(:last-child) {
    border-right: 1px solid #e5e5e5;
  }
`;

const DataGridRenderer: React.FC<DataGridRendererProps> = ({ block }) => {
  const { description, rows, columns } = block.values;

  const {
    chatId,
    handlers: { handleSendChatMessage },
  } = useWiseOwlContext();

  const handleAction = (action: WiseOwlAction) => {
    if (action.type === WiseOwlActionType.UserSay && handleSendChatMessage) {
      handleSendChatMessage({ chatId, input: action.values.content });
    }
  };

  const getCellContent = (row: DataGridBlockRow, column: DataGridColumn) => {
    const action = row.actions ? row.actions[column.name] : undefined;
    const value = row.values[column.name] ?? "";

    if (action) {
      return (
        <Button variant="link" onClick={() => handleAction(action)}>
          {value}
        </Button>
      );
    }

    return value;
  };

  const getRowSpan = (row: DataGridBlockRow, column: DataGridColumn) => {
    if (row.spans) {
      return row.spans[column.name] ?? 1;
    }

    return 1;
  };

  return (
    <StyledContainer element={WiseOwlElementIds.WISE_OWL_DATA_GRID_BLOCK}>
      <Box marginBottom="space20">
        <PopoverContainer baseId="table-description-popover">
          <PopoverButton variant="link">
            <InformationIcon decorative={false} title="help" />
            What is this showing?
          </PopoverButton>
          <Popover aria-label="Popover">
            <Text as="p" whiteSpace="pre-wrap">
              {description}
            </Text>
          </Popover>
        </PopoverContainer>
      </Box>
      <DataGrid aria-label={description}>
        <DataGridHead>
          <DataGridRow>
            {columns.map((column, index) => (
              <DataGridHeader key={"header-" + index}>
                {column.displayName}
              </DataGridHeader>
            ))}
          </DataGridRow>
        </DataGridHead>
        <DataGridBody>
          {rows.map((row, rowIndex) => (
            <DataGridRow key={"row-" + rowIndex}>
              {columns.map(
                (column, colIndex) =>
                  getRowSpan(row, column) > 0 && (
                    <DataGridCell
                      key={"cell-" + rowIndex + "-" + colIndex}
                      rowSpan={getRowSpan(row, column)}
                    >
                      {getCellContent(row, column)}
                    </DataGridCell>
                  )
              )}
            </DataGridRow>
          ))}
        </DataGridBody>
      </DataGrid>
    </StyledContainer>
  );
};

export default DataGridRenderer;
